import { useCallback, useMemo } from 'react'

import {
  BannerLarge,
  Layout,
  Table,
  TextField,
  Tooltip,
  VisuallyHidden,
} from '@loadsmart/loadsmart-ui'
import { Dialog, Icon as MirandaReactIcon } from '@loadsmart/miranda-react'

import { calculateFlatRate, calculateRatePerDistance } from 'core/domain/LaneRate'
import { getDelivery, getExtraStops, getPickup } from 'core/domain/Stop'
import { formatRate, formatRatePerDistance } from 'core/presenters/laneRatePresenter'
import { formatDescriptionWithZip } from 'core/presenters/stopPresenter'
import { Icon } from 'core/ui/components/icon/Icon'
import { ExtraStopInformation } from 'core/ui/components/stop/ExtraStopInformation'
import { useInput } from 'core/ui/hooks/useInput'
import { getNumberFromString } from 'core/utils/number'

import { type Currency } from '../domain/Currency'
import { type DistanceUnit } from '../domain/DistanceUnit'
import {
  getInitialFlatRate,
  getInitialPerDistanceRate,
  hasLaneDetails,
  type Lane,
} from '../domain/Lane'
import {
  isRankProposal,
  isUnderNegotiation,
  shouldSubmitLaneProposal,
} from '../domain/LaneProposal'
import { type Mode } from '../domain/Mode'
import { isMultiCurrency } from '../domain/RequestForProposal'
import { getCurrencyPrefix, getInputStatus } from '../presenters/lanePresenter'
import { type SubmitLaneProposalParams } from '../use-cases/submitLaneProposal'
import { useLaneProposal } from './hooks/useLaneProposal'
import { RfpEquipment } from './RfpEquipment'
import { StyledRow } from './RfpLane.styles'
import { RfpLaneDetails } from './RfpLaneDetails'
import { InputWrapper } from './RfpLaneList.styles'
import { RfpLaneResult } from './RfpLaneResult'

export type RfpLaneProps = {
  index: number
  currency: Currency
  lane: Lane
  isBidDisabled: boolean
  rfpId: string
  shipperCompanyName: string
  displayResult: boolean
  enableCarrierFeedback?: boolean
  preferenceMode: Mode
  distanceUnit: DistanceUnit
}

export function RfpLane({
  index,
  currency,
  lane,
  isBidDisabled,
  rfpId,
  preferenceMode,
  shipperCompanyName,
  displayResult,
  distanceUnit,
}: RfpLaneProps) {
  /**
   * After releasing MultiMode, we will turn those values
   * from optional to required.
   */
  const equipmentSize = lane.preferences?.[0].equipmentSize

  const equipmentType = lane.preferences?.[0].equipmentType

  const preferenceId = lane.preferences?.[0]?.id
  const proposalPreference = lane.laneProposals?.find(
    proposal => proposal.lanePreferenceId === preferenceId
  )

  const proposal = proposalPreference

  const {
    hasCapacityError,
    hasRatePerMileError,
    hasOutOfSyncError,
    savedLaneProposal,
    isLoading,
    submitLaneProposal,
  } = useLaneProposal(proposal)

  const { laneId, volume, stops } = lane

  const multiCurrency = isMultiCurrency(currency)
  const isRank = isRankProposal(proposal)

  const { value: flatRateValue, setValue: setFlatRateValue } = useInput(
    getInitialFlatRate(multiCurrency, proposal)
  )

  const { value: ratePerDistanceValue, setValue: setRatePerDistanceValue } = useInput(
    getInitialPerDistanceRate(multiCurrency, proposal)
  )

  const { value: capacityValue, onChange: handleCapacityChange } = useInput(
    proposal?.capacity ?? volume
  )

  const origin = useMemo(() => getPickup(stops), [stops])

  const destination = useMemo(() => getDelivery(stops), [stops])

  const handleOnBlur = useCallback(async () => {
    const shouldSubmit = shouldSubmitLaneProposal(capacityValue, flatRateValue, savedLaneProposal)

    if (!shouldSubmit) return

    const controller = new AbortController()
    const submitParams: SubmitLaneProposalParams = {
      currentLaneProposal: savedLaneProposal,
      capacityValue,
      flatRateValue,
      laneId: lane.id,
      distanceUnit,
      ratePerDistanceValue,
      rfpId,
    }

    submitParams.preference = lane.preferences?.[0]

    await submitLaneProposal(submitParams, { controller })
  }, [
    capacityValue,
    flatRateValue,
    savedLaneProposal,
    lane.id,
    lane.preferences,
    distanceUnit,
    ratePerDistanceValue,
    rfpId,
    submitLaneProposal,
  ])

  const isLaneUnderNegotiation = isUnderNegotiation(proposal)
  const canUpdateRate = (isLaneUnderNegotiation || !isBidDisabled) && !isLoading

  const handleRateChange = useCallback(
    ({ currentTarget }: React.FocusEvent<HTMLInputElement>) => {
      if (!canUpdateRate) return

      const { name, value } = currentTarget

      if (name === 'ratePerDistance') {
        const ratePerDistanceNumber = getNumberFromString(value)
        const ratePerDistance = formatRatePerDistance(ratePerDistanceNumber)

        setRatePerDistanceValue(ratePerDistance)

        const calculatedFlatRate = calculateFlatRate(ratePerDistance, lane.distance)
        setFlatRateValue(calculatedFlatRate)
      } else {
        const flatRate = getNumberFromString(value)
        setFlatRateValue(flatRate)

        const calculatedRatePerDistance = calculateRatePerDistance(flatRate, lane.distance)
        setRatePerDistanceValue(calculatedRatePerDistance)
      }
    },
    [setFlatRateValue, setRatePerDistanceValue, canUpdateRate, lane.distance]
  )

  const currencyPrefix = getCurrencyPrefix(currency)

  return (
    <>
      <StyledRow
        data-testid="rfp-lane"
        key={lane.id}
        expandableContent={
          hasLaneDetails(lane) && <RfpLaneDetails preferenceMode={preferenceMode} lane={lane} />
        }
        $hasError={hasCapacityError || hasRatePerMileError}
      >
        <Table.Cell>
          {isRank && (
            <Tooltip message="Saved Rate">
              <Icon name="check-circle" size="24" color="primary" />
            </Tooltip>
          )}
        </Table.Cell>
        <Table.Cell>{formatDescriptionWithZip(origin)}</Table.Cell>
        <Table.Cell>
          <Layout.Group space="xs" align="center">
            <div>{formatDescriptionWithZip(destination)}</div>
            <ExtraStopInformation
              stops={getExtraStops(stops)}
              titleFormatter={formatDescriptionWithZip}
            />
          </Layout.Group>
        </Table.Cell>
        <Table.Cell>{laneId}</Table.Cell>
        <Table.Cell>{lane.distance}</Table.Cell>
        <Table.Cell>{lane.volume}</Table.Cell>
        <Table.Cell>
          <RfpEquipment
            equipmentType={equipmentType!}
            equipmentSize={equipmentSize!}
            rfpMode={preferenceMode}
          />
        </Table.Cell>
        <Table.Cell>
          <InputWrapper>
            <VisuallyHidden>
              <label htmlFor={`flat-rate-${lane.id}`}>flat rate</label>
            </VisuallyHidden>
            <TextField
              placeholder={currencyPrefix}
              value={formatRate(flatRateValue, false)}
              name="flatRate"
              onChange={handleRateChange}
              onClick={event => event.stopPropagation()}
              onBlur={handleOnBlur}
              maxLength={9}
              id={`flat-rate-${lane.id}`}
              disabled={!canUpdateRate}
              // @ts-expect-error
              status={getInputStatus(hasRatePerMileError)}
              scale="small"
            />
          </InputWrapper>
        </Table.Cell>
        <Table.Cell>
          <InputWrapper>
            <VisuallyHidden>
              <label htmlFor={`rate-per-distance-${lane.id}`}>rate per distance</label>
            </VisuallyHidden>
            <TextField
              placeholder={currencyPrefix}
              value={formatRate(ratePerDistanceValue, true)}
              name="ratePerDistance"
              onChange={handleRateChange}
              onClick={event => event.stopPropagation()}
              onBlur={handleOnBlur}
              maxLength={9}
              id={`rate-per-distance-${lane.id}`}
              disabled={!canUpdateRate}
              scale="small"
            />
          </InputWrapper>
        </Table.Cell>
        <Table.Cell>
          <InputWrapper>
            <VisuallyHidden>
              <label htmlFor={`capacity-${lane.id}`}>capacity</label>
            </VisuallyHidden>
            <TextField
              value={capacityValue}
              name="capacity"
              onChange={handleCapacityChange}
              onClick={event => event.stopPropagation()}
              onBlur={handleOnBlur}
              type="number"
              maxLength={9}
              id={`capacity-${lane.id}`}
              disabled={!canUpdateRate}
              // @ts-expect-error
              status={getInputStatus(hasCapacityError)}
              scale="small"
            />
          </InputWrapper>
          {hasOutOfSyncError && (
            <Dialog open={true} size="small" variant="neutral">
              <Dialog.Header>Page out of sync</Dialog.Header>
              <Dialog.Body style={{ whiteSpace: 'initial' }}>
                This page is out of sync with the latest updates. Refresh the page to stay in sync
                and save your changes properly.
              </Dialog.Body>
              <Dialog.Actions>
                <Dialog.ActionPrimary
                  leading={<MirandaReactIcon name="reloading" color="color-text-inverted" />}
                  onClick={() => window.location.reload()}
                >
                  Refresh page
                </Dialog.ActionPrimary>
              </Dialog.Actions>
            </Dialog>
          )}
        </Table.Cell>
        {displayResult && (
          <Table.Cell>
            <RfpLaneResult result={proposal?.varianceFromAcceptanceRate} />
          </Table.Cell>
        )}
      </StyledRow>
      {isLaneUnderNegotiation && (
        <Table.ExpandableRow>
          {/* @ts-expect-error */}
          <Table.Cell colSpan="10">
            <BannerLarge
              title={`${shipperCompanyName} made a counter offer on this lane. Review your rate and click on CHANGE OFFER to send it to the shipper.`}
              description={`"${lane.laneProposal?.negotiationNote}"`}
              variant="warning"
              icon={<Icon name="arrows-reverse" size="24" />}
            />
          </Table.Cell>
        </Table.ExpandableRow>
      )}
    </>
  )
}
